// @ts-nocheck
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import type { ContractConfiguration } from '@/types/ContractConfiguration';
import { POR_EVENTO_CONDITIONAL_TYPE } from '@/utils/financialOperationalImpact';

export interface FinancialEntryToCreate {
  description: string;
  amount: number;
  entry_type: 'receita';
  category: string;
  payment_date: string | null;
  status: 'Pendente' | 'Pago';
  client_id: string | null;
  case_id: string;
  is_installment: boolean;
  installments_count: number;
  installment_frequency: string;
  payment_notes: string | null;
  user_id: string;
  is_conditional_payment?: boolean;
  conditional_type?: string | null;
  auto_generated?: boolean;
}

// Função helper para mapear frequências para valores aceitos pelo banco
function mapInstallmentFrequency(frequency: 'weekly' | 'biweekly' | 'monthly'): string {
  const frequencyMap = {
    'weekly': 'semanal',
    'biweekly': 'quinzenal', 
    'monthly': 'mensal'
  };
  return frequencyMap[frequency] || 'mensal';
}

export async function createFinancialEntriesFromContract(
  contractConfig: ContractConfiguration,
  caseId: string,
  clientId: string | null,
  userId: string,
  installmentFrequency: 'weekly' | 'biweekly' | 'monthly' = 'monthly'
): Promise<FinancialEntryToCreate[]> {
  const mappedFrequency = mapInstallmentFrequency(installmentFrequency);
  const entries: FinancialEntryToCreate[] = [];

  // Função para calcular próxima data de parcela
  const getNextPaymentDate = (startDate: Date, installmentNumber: number, frequency: 'weekly' | 'biweekly' | 'monthly'): Date => {
    const newDate = new Date(startDate);
    
    switch (frequency) {
      case 'weekly':
        newDate.setDate(startDate.getDate() + (installmentNumber * 7));
        break;
      case 'biweekly':
        newDate.setDate(startDate.getDate() + (installmentNumber * 14));
        break;
      case 'monthly':
        newDate.setMonth(startDate.getMonth() + installmentNumber);
        break;
    }
    
    return newDate;
  };

  // Honorários Iniciais: uma entrada por parcela, cada uma com o valor da parcela
  if (contractConfig.hasInitialFee && contractConfig.initialFeeAmount > 0) {
    const count = Math.max(1, contractConfig.initialFeeInstallments || 1);
    const installmentValue = contractConfig.initialFeeAmount / count;
    // Datas salvas no banco voltam como texto; normaliza para Date
    const firstDate = contractConfig.initialFeeFirstPaymentDate
      ? new Date(contractConfig.initialFeeFirstPaymentDate)
      : null;
    const validFirstDate = firstDate && !isNaN(firstDate.getTime()) ? firstDate : null;

    for (let i = 0; i < count; i++) {
      const paymentDate = validFirstDate
        ? (i === 0 ? validFirstDate : getNextPaymentDate(validFirstDate, i, installmentFrequency))
        : null;

      entries.push({
        description: count > 1
          ? `Honorários Iniciais - Parcela ${i + 1}/${count}`
          : 'Honorários Iniciais - À Vista',
        amount: installmentValue,
        entry_type: 'receita',
        category: 'Honorários',
        payment_date: paymentDate ? format(paymentDate, 'yyyy-MM-dd') : null,
        status: i === 0 && contractConfig.initialFeeAlreadyPaid ? 'Pago' : 'Pendente',
        client_id: clientId,
        case_id: caseId,
        is_installment: count > 1,
        installments_count: count,
        installment_frequency: mappedFrequency,
        payment_notes: `Método: ${contractConfig.initialFeePaymentMethod || 'pix'}`,
        user_id: userId
      });
    }
  }

  // Contrato Principal
  if (contractConfig.mode === 'global_fixed' && contractConfig.globalValue > 0) {
    const lumpSum = !contractConfig.isInstallments;
    const isPaid = lumpSum
      ? !!contractConfig.globalAlreadyPaid
      : !!contractConfig.initialFeeAlreadyPaid;
    
    entries.push({
      description: `Contrato Principal - Valor Global`,
      amount: contractConfig.globalValue,
      entry_type: 'receita',
      category: 'Honorários',
      payment_date: contractConfig.firstPaymentDate ? format(contractConfig.firstPaymentDate, 'yyyy-MM-dd') : null,
      status: isPaid ? 'Pago' : 'Pendente',
      client_id: clientId,
      case_id: caseId,
      is_installment: false,
      installments_count: 1,
      installment_frequency: mappedFrequency,
      payment_notes: `Método: ${contractConfig.paymentMethod || 'pix'}`,
      user_id: userId
    });
  }

  if (contractConfig.mode === 'specific_installments') {
    const isPaid = contractConfig.initialFeeAlreadyPaid;
    
    for (let i = 0; i < contractConfig.installmentCount; i++) {
      const paymentDate = contractConfig.firstPaymentDate 
        ? getNextPaymentDate(contractConfig.firstPaymentDate, i, installmentFrequency)
        : null;

      const entry = {
        description: `Contrato Principal - Parcela ${i + 1}/${contractConfig.installmentCount}`,
        amount: contractConfig.installmentValue,
        entry_type: 'receita',
        category: 'Honorários',
        payment_date: paymentDate ? format(paymentDate, 'yyyy-MM-dd') : null,
        status: i === 0 && isPaid ? 'Pago' : 'Pendente',
        client_id: clientId,
        case_id: caseId,
        is_installment: true,
        installments_count: contractConfig.installmentCount,
        installment_frequency: mappedFrequency,
        payment_notes: `Método: ${contractConfig.paymentMethod || 'pix'}`,
        user_id: userId
      };
      
      entries.push(entry);
    }
  }

  // % do benefício: uma parcela de honorário por parcela do benefício (ex.: 30% de cada salário-maternidade)
  if (contractConfig.mode === 'success_fee' && contractConfig.successType === 'benefit') {
    const count = Math.max(1, Math.round(contractConfig.salaryCount || 1));
    const pct = contractConfig.benefitPercentage ?? contractConfig.successPercentage ?? 0;
    const each = Math.round((pct / 100) * (contractConfig.salaryValue || 0) * 100) / 100;
    const first = contractConfig.benefitFirstPaymentDate ? new Date(contractConfig.benefitFirstPaymentDate as any) : null;
    for (let i = 0; i < count; i++) {
      const date = first && !isNaN(first.getTime()) ? new Date(first.getFullYear(), first.getMonth() + i, first.getDate()) : null;
      entries.push({
        description: `Honorários de Êxito - ${pct}% do benefício (parcela ${i + 1}/${count})`,
        amount: each,
        entry_type: 'receita',
        category: 'Honorários',
        payment_date: date ? format(date, 'yyyy-MM-dd') : null,
        status: 'Pendente',
        client_id: clientId,
        case_id: caseId,
        is_installment: true,
        installments_count: count,
        installment_frequency: mappedFrequency,
        payment_notes: 'Cobrado conforme a cliente recebe cada parcela do benefício',
        user_id: userId
      });
    }
  } else if (contractConfig.mode === 'success_fee') {
    const hasEstimate = contractConfig.hasEstimate && contractConfig.estimatedValue && contractConfig.estimatedValue > 0;
    const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const basis = contractConfig.successType === 'salaries'
      ? `${contractConfig.salaryCount ?? 0} salário(s)`
      : `${contractConfig.successPercentage ?? 0}%`;

    entries.push({
      description: hasEstimate
        ? `Honorários de Êxito - ${basis} (Estimativa: ${brl(contractConfig.estimatedValue!)})`
        : `Honorários de Êxito - ${basis} - A definir`,
      amount: hasEstimate ? contractConfig.estimatedValue : 0,
      entry_type: 'receita',
      category: 'Honorários',
      payment_date: null,
      status: 'Pendente',
      client_id: clientId,
      case_id: caseId,
      is_installment: false,
      installments_count: 1,
      installment_frequency: mappedFrequency,
      payment_notes: 'Valor será definido após resultado do processo',
      user_id: userId
    });
  }

  if (contractConfig.mode === 'percentage_case') {
    const pct = contractConfig.percentage ?? 0;
    const percentageValue = (pct / 100) * contractConfig.caseValue;
    
    entries.push({
      description: `Honorários - ${pct}% do Valor da Causa`,
      amount: percentageValue,
      entry_type: 'receita',
      category: 'Honorários',
      payment_date: contractConfig.firstPaymentDate ? format(contractConfig.firstPaymentDate, 'yyyy-MM-dd') : null,
      status: contractConfig.initialFeeAlreadyPaid ? 'Pago' : 'Pendente',
      client_id: clientId,
      case_id: caseId,
      is_installment: false,
      installments_count: 1,
      installment_frequency: mappedFrequency,
      payment_notes: `Método: ${contractConfig.paymentMethod || 'pix'}`,
      user_id: userId
    });
  }

  if (contractConfig.mode === 'conditional_events') {
    const trigger = (contractConfig.triggerDescription || 'Evento acordado').trim().slice(0, 200);
    const baseNote = `Cobrança por evento (fora do fluxo até o Financeiro). Disparo: ${trigger}`;
    const totalValue = contractConfig.totalValue ?? 0;
    const isZeroValue = totalValue <= 0;

    if (!contractConfig.isInstallments || isZeroValue) {
      entries.push({
        description: `Contrato — cobrança condicional (${trigger.slice(0, 80)})`,
        amount: totalValue,
        entry_type: 'receita',
        category: 'Honorários',
        payment_date: null,
        status: 'Pendente',
        client_id: clientId,
        case_id: caseId,
        is_installment: false,
        installments_count: 1,
        installment_frequency: mappedFrequency,
        payment_notes: isZeroValue
          ? `${baseNote}. Valor zerado registrado como pendente para controle, sem cobrança imediata.`
          : `${baseNote}. À vista quando o evento ocorrer — edite o lançamento para datas ou previsão.`,
        user_id: userId,
        is_conditional_payment: true,
        conditional_type: POR_EVENTO_CONDITIONAL_TYPE,
        auto_generated: true,
      });
    } else {
      const n = Math.max(1, contractConfig.installmentCount || 1);
      const each = totalValue / n;
      const freq = contractConfig.installmentFrequency || installmentFrequency;

      for (let i = 0; i < n; i++) {
        entries.push({
          description: `Contrato condicional — Parcela ${i + 1}/${n} (${trigger.slice(0, 60)})`,
          amount: each,
          entry_type: 'receita',
          category: 'Honorários',
          payment_date: null,
          status: 'Pendente',
          client_id: clientId,
          case_id: caseId,
          is_installment: true,
          installments_count: n,
          installment_frequency: mapInstallmentFrequency(freq),
          payment_notes: `${baseNote}. Parcela ${i + 1}/${n}. Informe datas ou previsão ao editar no Financeiro.`,
          user_id: userId,
          is_conditional_payment: true,
          conditional_type: POR_EVENTO_CONDITIONAL_TYPE,
          auto_generated: true,
        });
      }
    }
  }

  return entries;
}

export async function saveFinancialEntries(entries: FinancialEntryToCreate[]): Promise<void> {
  if (entries.length === 0) return;

  for (const entry of entries) {
    const { error } = await supabase
      .from('financial_entries')
      .insert(entry);

    if (error) {
      throw new Error(`Erro ao criar lançamentos financeiros: ${error.message}`);
    }
  }
}
